import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileText, Upload, X, Download, Trash2, Edit } from "lucide-react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import { toast } from "sonner";

interface OnboardingDocumentUploaderProps {
  onboardingId: Id<"clientOnboarding">;
}

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Helper function to get status badge color
const getStatusBadgeColor = (status: string) => {
  switch (status) {
    case "draft":
      return "bg-gray-100 text-gray-800";
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    case "approved":
      return "bg-green-100 text-green-800";
    case "signed":
      return "bg-blue-100 text-blue-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export function OnboardingDocumentUploader({ onboardingId }: OnboardingDocumentUploaderProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [editingDocumentId, setEditingDocumentId] = useState<string | null>(null);
  const [newStatus, setNewStatus] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get the documents for this onboarding
  const documents =
    useQuery(api.onboardingDocuments.getDocumentsByOnboarding, {
      onboardingId,
    }) || [];

  // Generate upload URL mutation
  const generateUploadUrl = useMutation(api.onboardingDocuments.generateUploadUrl);

  // Create document record mutation
  const createDocument = useMutation(api.onboardingDocuments.createDocument);

  // Update document status mutation
  const updateDocumentStatus = useMutation(api.onboardingDocuments.updateDocumentStatus);

  // Delete document mutation
  const deleteDocument = useMutation(api.onboardingDocuments.deleteDocument);

  // Handle file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      // Step 1: Get a URL to upload the file to
      const uploadUrl = await generateUploadUrl();

      // Step 2: Upload the file to the URL
      const uploadResponse = await fetch(uploadUrl, {
        method: "POST",
        headers: {
          "Content-Type": file.type,
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }

      const { storageId } = await uploadResponse.json();

      // Step 3: Create a document record in the database
      await createDocument({
        onboardingId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        storageId,
      });

      setUploadProgress(100);
      toast.success("Document uploaded successfully!", {
        description: `${file.name} has been uploaded and is ready for review.`,
      });
    } catch (err) {
      console.error("Error uploading file:", err);
      setError("Failed to upload file. Please try again.");
      toast.error("Upload failed", {
        description: "Please check your connection and try again.",
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Handle document download
  const handleDownload = async (storageId: string, fileName: string) => {
    try {
      // For now, we'll create a simple download by opening the file in a new tab
      // In a production environment, you'd want to implement proper download URLs
      const url = `${import.meta.env.VITE_CONVEX_URL}/api/storage/${storageId}`;

      // Create a temporary anchor element to trigger the download
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error("Error downloading file:", err);
      setError("Failed to download file");
    }
  };

  // Handle document deletion
  const handleDelete = async (id: string) => {
    try {
      await deleteDocument({ id: id as Id<"onboardingDocuments"> });
      toast.success("Document deleted successfully");
    } catch (err) {
      console.error("Error deleting document:", err);
      setError("Failed to delete document");
      toast.error("Failed to delete document", {
        description: "Please try again or refresh the page.",
      });
    }
  };

  // Handle status update
  const handleStatusUpdate = async (documentId: string, status: string) => {
    try {
      await updateDocumentStatus({
        id: documentId as Id<"onboardingDocuments">,
        status: status as "draft" | "pending" | "approved" | "signed",
      });
      setEditingDocumentId(null);
      setNewStatus("");
      toast.success("Document status updated!", {
        description: `Status changed to ${status}.`,
      });
    } catch (err) {
      console.error("Error updating document status:", err);
      setError("Failed to update document status");
      toast.error("Failed to update status", {
        description: "Please try again or refresh the page.",
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Documents</h3>
        <div className="flex items-center gap-2">
          <Input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            className="hidden"
            id="file-upload"
            disabled={isUploading}
          />
          <Button
            size="sm"
            className="gap-2"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            <Upload className="h-4 w-4" /> Upload Document
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-center justify-between rounded-md bg-red-50 p-3 text-red-700">
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={() => setError(null)} className="h-6 w-6 p-0">
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {isUploading && (
        <div className="rounded-md bg-blue-50 p-3 text-blue-700">
          <div className="mb-2 flex items-center justify-between">
            <span>Uploading...</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="h-2 w-full rounded-full bg-blue-200">
            <div
              className="h-2 rounded-full bg-blue-600 transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {documents.length === 0 ? (
        <Card className="flex flex-col items-center justify-center border-dashed p-8 text-center">
          <FileText className="mb-4 h-12 w-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium">No documents uploaded yet</h3>
          <p className="mb-4 text-gray-500">
            Upload documents to keep track of important files for this onboarding
          </p>
          <Button variant="outline" className="gap-2" onClick={() => fileInputRef.current?.click()}>
            <Upload className="h-4 w-4" /> Upload Document
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {documents.map((doc: any) => (
            <Card key={doc._id} className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                    <FileText className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">{doc.fileName}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <span>{formatFileSize(doc.fileSize)}</span>
                      <span>•</span>
                      <span>{new Date(doc.createdAt).toLocaleDateString()}</span>
                      <span>•</span>
                      <span>by {doc.uploadedByName}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {editingDocumentId === doc._id ? (
                    <div className="flex items-center gap-2">
                      <Select value={newStatus} onValueChange={setNewStatus}>
                        <SelectTrigger className="w-32">
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="signed">Signed</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        onClick={() => handleStatusUpdate(doc._id, newStatus)}
                        disabled={!newStatus}
                      >
                        Save
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingDocumentId(null);
                          setNewStatus("");
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Badge className={getStatusBadgeColor(doc.status)}>
                        {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => {
                          setEditingDocumentId(doc._id);
                          setNewStatus(doc.status);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => handleDownload(doc.storageId, doc.fileName)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => handleDelete(doc._id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
